import { useEffect, useState, useMemo } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPublic, apiPrivate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, ThumbsUp, ThumbsDown } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import DOMPurify from "dompurify";

const CATEGORIES = [
    { k: "all", label: "Tutte" },
    { k: "quote", label: "Quote" },
    { k: "assemblee", label: "Assemblee" },
    { k: "fiscalita", label: "Fiscalità" },
    { k: "piattaforma", label: "Piattaforma" },
    { k: "visura", label: "Visura" },
    { k: "altro", label: "Altro" },
];

export default function Faq() {
    const { user } = useAuth();
    const [faqs, setFaqs] = useState([]);
    const [search, setSearch] = useState("");
    const [cat, setCat] = useState("all");
    const [voted, setVoted] = useState({});

    const client = user ? apiPrivate : apiPublic;

    useEffect(() => {
        client.get("/faq").then(r => setFaqs(r.data)).catch(() => {});
    }, []);

    const filtered = useMemo(() => faqs.filter(f =>
        (cat === "all" || f.category === cat) &&
        (search.trim() === "" ||
            f.question.toLowerCase().includes(search.toLowerCase()) ||
            f.answer.toLowerCase().includes(search.toLowerCase()))
    ), [faqs, search, cat]);

    const submitVote = async (id, helpful) => {
        if (voted[id]) return;
        try {
            await client.post(`/faq/${id}/feedback`, { helpful });
            setVoted(v => ({ ...v, [id]: helpful ? "yes" : "no" }));
        } catch {}
    };

    const Wrapper = user ? AppLayout : PublicShell;
    return (
        <Wrapper>
            <div className="max-w-4xl mx-auto" data-testid="faq-page">
                <span className="label-tag">— Domande frequenti</span>
                <h1 className="font-display text-5xl lg:text-6xl mt-3 tracking-tighter">Risposte <span className="font-serif-italic text-acid">veloci</span>.</h1>
                <p className="text-zinc-400 mt-4 max-w-xl">Cerca, filtra per categoria, dicci se la risposta è stata utile.</p>

                <div className="mt-10 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-600" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Cerca una domanda…"
                        data-testid="faq-search"
                        className="w-full bg-[#0F0F0F] border border-zinc-800 text-white pl-12 pr-4 py-4 font-grotesk focus:outline-none focus:border-acid"
                    />
                </div>

                <div className="mt-6 flex gap-2 flex-wrap" data-testid="faq-categories">
                    {CATEGORIES.map(c => (
                        <button
                            key={c.k}
                            onClick={() => setCat(c.k)}
                            data-testid={`faq-cat-${c.k}`}
                            className={`px-3 py-2 text-xs uppercase tracking-widest font-grotesk border transition ${
                                cat === c.k ? "border-acid text-acid" : "border-zinc-800 text-zinc-500 hover:text-white"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                <Accordion type="single" collapsible className="mt-8 space-y-2" data-testid="faq-accordion">
                    {filtered.length === 0 ? (
                        <div className="bento p-10 text-center text-zinc-500" data-testid="faq-empty">Nessuna FAQ trovata.</div>
                    ) : filtered.map(f => (
                        <AccordionItem key={f.id} value={f.id} className="bento border data-[state=open]:border-acid transition-colors" data-testid={`faq-item-${f.id}`}>
                            <AccordionTrigger className="font-grotesk text-left hover:no-underline px-6 py-5" data-testid={`faq-trigger-${f.id}`}>
                                <span>{f.question}</span>
                            </AccordionTrigger>
                            <AccordionContent className="px-6 pb-6 text-zinc-300 leading-relaxed">
                                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(f.answer.replace(/\n/g, "<br/>"), { ALLOWED_TAGS: ["br", "b", "i", "em", "strong", "u", "a", "p", "code", "ul", "ol", "li"], ALLOWED_ATTR: ["href", "target", "rel"] }) }} />
                                <div className="mt-6 flex items-center gap-4 pt-4 border-t border-zinc-900">
                                    <span className="text-xs uppercase tracking-widest font-grotesk text-zinc-500">È stata utile?</span>
                                    <button
                                        onClick={() => submitVote(f.id, true)}
                                        disabled={!!voted[f.id]}
                                        data-testid={`faq-vote-yes-${f.id}`}
                                        className={`flex items-center gap-2 text-xs uppercase tracking-widest font-grotesk px-3 py-2 border transition ${voted[f.id] === "yes" ? "border-acid text-acid" : "border-zinc-800 text-zinc-500 hover:border-acid hover:text-acid"} disabled:opacity-50`}
                                    >
                                        <ThumbsUp className="h-3 w-3" /> Sì
                                    </button>
                                    <button
                                        onClick={() => submitVote(f.id, false)}
                                        disabled={!!voted[f.id]}
                                        data-testid={`faq-vote-no-${f.id}`}
                                        className={`flex items-center gap-2 text-xs uppercase tracking-widest font-grotesk px-3 py-2 border transition ${voted[f.id] === "no" ? "border-magenta text-magenta" : "border-zinc-800 text-zinc-500 hover:border-magenta hover:text-magenta"} disabled:opacity-50`}
                                    >
                                        <ThumbsDown className="h-3 w-3" /> No
                                    </button>
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </Wrapper>
    );
}

function PublicShell({ children }) {
    return <div className="min-h-screen bg-[#050505] text-white p-6 lg:p-12">{children}</div>;
}
